import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { useEffect, useRef, useState } from 'react'

import {
  setCacheVersion,
  setStockCacheVersion,
} from '@utilities/cache-version-utils'

import firestore from './firebase-db'

const DEVSUITE_COLLECTION = 'devsuite'
const CACHE_VERSION_DOCUMENT_ID = 'general'
const STOCK_CACHE_VERSION_DOCUMENT_ID = 'stock'

export interface CacheVersionGeneral {
  history: string[]
  version: string
}

export interface CacheVersionResult {
  version: string
  stockCacheVersion: string
}

const CACHE_VERSION_DEFAULT: CacheVersionGeneral = {
  history: [],
  version: '',
}

const getDocRef = (projectName: string, subDocId: string) =>
  doc(firestore, DEVSUITE_COLLECTION, projectName, 'cache-version', subDocId)

export const fetchCacheVersionFromFirebase = async (
  projectName: string
): Promise<CacheVersionResult> => {
  if (!firestore || !projectName) return { version: '', stockCacheVersion: '' }
  try {
    const [generalSnap, stockSnap] = await Promise.all([
      getDoc(getDocRef(projectName, CACHE_VERSION_DOCUMENT_ID)),
      getDoc(getDocRef(projectName, STOCK_CACHE_VERSION_DOCUMENT_ID)),
    ])
    const version = generalSnap.exists()
      ? ((generalSnap.data() as CacheVersionGeneral)?.version ?? '')
      : ''
    const stockCacheVersion = stockSnap.exists()
      ? ((stockSnap.data() as CacheVersionGeneral)?.version ?? '')
      : ''
    return { version, stockCacheVersion }
  } catch (error) {
    console.error('Error:', error)
  }
  return { version: '', stockCacheVersion: '' }
}

const useFirebaseCacheVersionService = (
  projectName: string,
  enabled: boolean,
  onStockCacheVersionChange?: (newVersion: string) => void
) => {
  const [cacheVersionData, setCacheVersionData] =
    useState<CacheVersionGeneral>()

  const onStockCacheVersionChangeRef = useRef(onStockCacheVersionChange)
  useEffect(() => {
    onStockCacheVersionChangeRef.current = onStockCacheVersionChange
  }, [onStockCacheVersionChange])

  useEffect(() => {
    if (!enabled || !projectName) {
      setCacheVersionData(undefined)
      return () => {}
    }

    const docRef = getDocRef(projectName, CACHE_VERSION_DOCUMENT_ID)
    const unsub = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as CacheVersionGeneral
          setCacheVersionData({
            history: data?.history ?? [],
            version: data?.version ?? '',
          })
          if (data?.version != null) {
            setCacheVersion(data.version)
          }
        } else {
          setCacheVersionData(CACHE_VERSION_DEFAULT)
        }
      },
      () => {
        setCacheVersionData(CACHE_VERSION_DEFAULT)
      }
    )

    return () => {
      if (unsub) {
        unsub()
      }
    }
  }, [projectName, enabled])

  useEffect(() => {
    if (!enabled || !projectName) return () => {}

    const stockDocRef = getDocRef(projectName, STOCK_CACHE_VERSION_DOCUMENT_ID)
    const unsub = onSnapshot(
      stockDocRef,
      (snapshot) => {
        const newVersion = snapshot.exists()
          ? ((snapshot.data() as CacheVersionGeneral)?.version ?? '')
          : ''
        setStockCacheVersion(newVersion)
        onStockCacheVersionChangeRef.current?.(newVersion)
      },
      () => {}
    )

    return () => {
      if (unsub) {
        unsub()
      }
    }
  }, [projectName, enabled])

  return {
    cacheVersionData,
  }
}

export default useFirebaseCacheVersionService
